import { ExclamationTriangleIcon, XCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { cn } from '@/shared/utils/cn';

export interface ErrorAlertProps {
  title?: string;
  message: string;
  variant?: 'error' | 'warning';
  onDismiss?: () => void;
  className?: string;
}

export function ErrorAlert({
  title,
  message,
  variant = 'error',
  onDismiss,
  className,
}: ErrorAlertProps) {
  const isError = variant === 'error';

  return (
    <div
      className={cn(
        'rounded-md p-4',
        isError ? 'bg-red-50 dark:bg-red-900/20' : 'bg-yellow-50 dark:bg-yellow-900/20',
        className
      )}
    >
      <div className="flex">
        <div className="flex-shrink-0">
          {isError ? (
            <XCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
          ) : (
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" aria-hidden="true" />
          )}
        </div>
        <div className="ml-3">
          {title && (
            <h3
              className={cn(
                'text-sm font-medium',
                isError ? 'text-red-800 dark:text-red-300' : 'text-yellow-800 dark:text-yellow-300'
              )}
            >
              {title}
            </h3>
          )}
          <div
            className={cn(
              'text-sm',
              isError ? 'text-red-700 dark:text-red-400' : 'text-yellow-700 dark:text-yellow-400',
              title && 'mt-2'
            )}
          >
            <p>{message}</p>
          </div>
        </div>
        {onDismiss && (
          <div className="ml-auto pl-3">
            <div className="-mx-1.5 -my-1.5">
              <button
                type="button"
                onClick={onDismiss}
                className={cn(
                  'inline-flex rounded-md p-1.5 focus:outline-none focus:ring-2 focus:ring-offset-2',
                  isError
                    ? 'bg-red-50 dark:bg-red-900/30 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/50 focus:ring-red-600 focus:ring-offset-red-50 dark:focus:ring-offset-gray-900'
                    : 'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-500 hover:bg-yellow-100 dark:hover:bg-yellow-900/50 focus:ring-yellow-600 focus:ring-offset-yellow-50 dark:focus:ring-offset-gray-900'
                )}
              >
                <span className="sr-only">Dismiss</span>
                <XMarkIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
